const parsePositiveNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

const normalizeValue = (value = "") => String(value || "").trim().toLowerCase();
const isTruthyPaidValue = (value) => {
  const normalized = normalizeValue(value);
  return ["1", "true", "yes", "paid", "active"].includes(normalized);
};

const parseDisplayYearToCanonicalId = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value === 0) return 1;
    return value > 0 ? value + 1 : 0;
  }

  const normalized = normalizeValue(value);
  if (!normalized) return 0;
  if (normalized === "foundation") return 1;

  const yearLabelMatch = normalized.match(/^year\s*(\d+)$/);
  if (yearLabelMatch) {
    return parsePositiveNumber(yearLabelMatch[1]) + 1;
  }

  const numericValue = Number(normalized);
  if (!Number.isFinite(numericValue)) return 0;
  if (numericValue === 0) return 1;
  return numericValue > 0 ? numericValue + 1 : 0;
};

export const BASIC_PLAN_START_YEAR_ID = 1;

export const getCanonicalYearId = (year = {}) =>
  parsePositiveNumber(year?.fields?.yearId ?? year?.pk ?? year?.id);

export const getCurrentUnlockedYearId = (user = {}) => {
  const directYearId = parsePositiveNumber(
    user?.yearId ??
      user?.YearId ??
      user?.year_id ??
      user?.yearid ??
      user?.current_year_id ??
      user?.currentYearId ??
      user?.currentYearID
  );

  if (directYearId) return directYearId;

  return parseDisplayYearToCanonicalId(
    user?.year ?? user?.current_year ?? user?.currentYear ?? user?.grade
  );
};

export const getAccessibleBasicYearId = (
  user = {},
  minimumYearId = BASIC_PLAN_START_YEAR_ID
) => {
  const currentUnlockedYearId = getCurrentUnlockedYearId(user);
  const normalizedMinimumYearId = parsePositiveNumber(minimumYearId);

  if (currentUnlockedYearId && normalizedMinimumYearId) {
    return Math.max(currentUnlockedYearId, normalizedMinimumYearId);
  }

  return currentUnlockedYearId || normalizedMinimumYearId;
};

export const getUserId = (user = {}) =>
  parsePositiveNumber(
    user?.UserId ?? user?.pk ?? user?.id ?? user?.user_id ?? user?.userid
  );

export const getUserPlanType = (user = {}) => {
  const planName = normalizeValue(
    user?.plan ??
      user?.Plan ??
      user?.plan_type ??
      user?.planType ??
      user?.plan_name ??
      user?.subscription_plan ??
      user?.price_plan
  );

  if (planName.includes("pro")) return "pro";
  if (planName.includes("basic")) return "basic";
  return "";
};

export const formatYearLabel = (yearId) => {
  const parsedYearId = parsePositiveNumber(yearId);
  if (!parsedYearId) return "";
  if (parsedYearId === 1) return "Foundation";
  return `Year ${parsedYearId - 1}`;
};

export const getYearLabelFromData = (year = {}) => {
  const rawYearLabel = year?.fields?.year;
  if (typeof rawYearLabel === "string") {
    const trimmedLabel = rawYearLabel.trim();
    if (trimmedLabel) {
      const normalizedLabel = trimmedLabel.toLowerCase();
      if (
        normalizedLabel.includes("year") ||
        normalizedLabel.includes("foundation")
      ) {
        return trimmedLabel;
      }

      const numericLabel = Number(trimmedLabel);
      if (Number.isFinite(numericLabel)) {
        return numericLabel === 0 ? "Foundation" : `Year ${numericLabel}`;
      }

      return trimmedLabel;
    }
  }

  if (typeof rawYearLabel === "number") {
    return rawYearLabel === 0 ? "Foundation" : `Year ${rawYearLabel}`;
  }

  return formatYearLabel(getCanonicalYearId(year));
};

export const getNextYearMeta = (currentYearId, years = []) => {
  const normalizedCurrentYearId = parsePositiveNumber(currentYearId);
  if (!normalizedCurrentYearId) return null;

  const currentYearIndex = years.findIndex(
    (year) => getCanonicalYearId(year) === normalizedCurrentYearId
  );
  const nextYear = currentYearIndex >= 0 ? years[currentYearIndex + 1] : null;

  if (nextYear) {
    return {
      yearid: getCanonicalYearId(nextYear),
      year: getYearLabelFromData(nextYear),
    };
  }

  const fallbackNextYearId = normalizedCurrentYearId + 1;
  return {
    yearid: fallbackNextYearId,
    year: formatYearLabel(fallbackNextYearId),
  };
};

export const isPlanExpired = (value) => {
  if (!value) return false;

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return false;

  const parsedDate = new Date(`${normalizedValue}T23:59:59`);
  if (Number.isNaN(parsedDate.getTime())) return false;

  return parsedDate.getTime() < Date.now();
};

export const isPaidBasicUser = (user = {}) => {
  const isPaid = isTruthyPaidValue(
    user?.isPaid ?? user?.ispaid ?? user?.is_paid ?? user?.paid_status
  );
  const planExpiryDate =
    user?.plan_expiry_date ?? user?.planExpiryDate ?? user?.planExpiry;

  if (!isPaid) return false;
  if (getUserPlanType(user) !== "basic") return false;
  if (isPlanExpired(planExpiryDate)) return false;

  return true;
};

export const isPaidProUser = (user = {}) => {
  const isPaid = isTruthyPaidValue(
    user?.isPaid ?? user?.ispaid ?? user?.is_paid ?? user?.paid_status
  );
  const planExpiryDate =
    user?.plan_expiry_date ?? user?.planExpiryDate ?? user?.planExpiry;

  if (!isPaid) return false;
  if (getUserPlanType(user) !== "pro") return false;
  if (isPlanExpired(planExpiryDate)) return false;

  return true;
};

export const hasPaidPlanAccess = (user = {}) =>
  isPaidBasicUser(user) || isPaidProUser(user);
